import { useState } from 'react';
import { Box, Typography, Card, CardContent, Collapse, IconButton, Chip } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { formatDias } from '../../utils/formatters';

// Controle de Carga — Contratos com vários projetos (Fase 3).
// Cada linha é um contrato: carga total, quanto os projetos já usaram juntos, workshops e risco.
// Ao abrir a linha aparece cada projeto e quanto ele usou do contrato.

const RISCO = {
  ritmo:   { label: 'No ritmo', cor: '#16A34A' },
  atencao: { label: 'Atenção',  cor: '#D97706' },
  critico: { label: 'Crítico',  cor: '#DC2626' },
};

const SITUACAO = {
  aguardando_projeto: { label: 'Aguardando projeto', cor: '#BA7517', fundo: '#FAEEDA' },
  aguardando_inicio:  { label: 'Aguardando início',  cor: '#BA7517', fundo: '#FAEEDA' },
  em_andamento:       { label: 'Em andamento',       cor: '#2563EB', fundo: '#E8F0FD' },
  pausado:            { label: 'Pausado',            cor: '#D97706', fundo: '#FAEEDA' },
};

const STATUS_PROJETO = {
  planejamento: 'Planejamento',
  em_andamento: 'Em andamento',
  pausado: 'Pausado',
  concluido: 'Concluído',
  cancelado: 'Cancelado',
};

const real = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

function Barra({ rotulo, pct, cor }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.7 }}>
      <Typography sx={{ fontSize: 11, color: '#94a3b8', width: 78, flexShrink: 0 }}>{rotulo}</Typography>
      <Box sx={{ flex: 1, height: 8, borderRadius: 4, backgroundColor: '#eef0f3', overflow: 'hidden' }}>
        <Box sx={{ height: '100%', width: `${Math.min(pct, 100)}%`, backgroundColor: cor, borderRadius: 4 }} />
      </Box>
      <Typography sx={{ fontSize: 12, fontFamily: 'monospace', color: '#1a1a2e', width: 40, textAlign: 'right' }}>{Math.round(pct)}%</Typography>
    </Box>
  );
}

function LinhaContrato({ contrato, mes, ano, nomeMes }) {
  const [aberto, setAberto] = useState(false);
  const sit = SITUACAO[contrato.situacao] || SITUACAO.em_andamento;
  const semProjeto = contrato.situacao === 'aguardando_projeto';
  const emAndamento = contrato.situacao === 'em_andamento';
  const risco = RISCO[contrato.risco] || RISCO.ritmo;
  const saldo = contrato.carga_total - contrato.carga_usada;
  const recebidoMes = Number((contrato.recebido_por_mes || {})[`${ano}-${String(mes).padStart(2, '0')}`] || 0);

  return (
    <Box component="li" sx={{ py: 1.4, borderTop: '1px solid #eef0f3', '&:first-of-type': { borderTop: 'none', pt: 0 } }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <IconButton
          size="small" onClick={() => setAberto((v) => !v)}
          aria-label={`${aberto ? 'Recolher' : 'Ver'} projetos de ${contrato.nome}`} aria-expanded={aberto}
          sx={{ mt: -0.2, transform: aberto ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s ease' }}
        >
          <ExpandMoreIcon fontSize="small" />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#1a1a2e' }}>{contrato.nome}</Typography>
                <Chip size="small" label={sit.label} sx={{ height: 20, fontSize: 11, fontWeight: 700, color: sit.cor, backgroundColor: sit.fundo }} />
                <Typography sx={{ fontSize: 12, color: '#64748b' }}>
                  {contrato.projetos.length} {contrato.projetos.length === 1 ? 'projeto' : 'projetos'}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: 12, color: '#64748b' }}>
                {contrato.cliente_nome}{contrato.celula_nome ? ` · ${contrato.celula_nome}` : ' · célula ainda não definida'}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography sx={{ fontSize: 13.5, fontWeight: 700, fontFamily: 'monospace', color: '#13375F' }}>{real(contrato.valor_contrato)}</Typography>
              <Typography sx={{ fontSize: 12, color: '#64748b' }}>valor total do contrato</Typography>
            </Box>
          </Box>

          {!semProjeto && (
            <>
              <Barra rotulo="Carga usada" pct={contrato.pct_carga} cor="#e94560" />
              {contrato.workshops_vendidos > 0 && <Barra rotulo="Workshops" pct={contrato.pct_workshop} cor="#2563EB" />}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.7, flexWrap: 'wrap', gap: 1 }}>
                <Typography sx={{ fontSize: 12, color: '#64748b' }}>
                  Carga: <strong>{formatDias(contrato.carga_usada)}</strong> de <strong>{formatDias(contrato.carga_total)}</strong>
                  {' · '}Saldo: <strong>{formatDias(saldo)}</strong>
                  {contrato.workshops_vendidos > 0 && (
                    <>{' · '}Workshops: <strong>{contrato.workshops_realizados}</strong> de <strong>{contrato.workshops_vendidos}</strong></>
                  )}
                  {' · '}Recebido em {nomeMes.toLowerCase()}: <strong>{real(recebidoMes)}</strong>
                  {' · '}Acumulado: <strong>{real(contrato.recebido_total)}</strong>
                </Typography>
                {emAndamento && <Typography sx={{ fontSize: 12, fontWeight: 700, color: risco.cor }}>● {risco.label}</Typography>}
              </Box>
            </>
          )}
          {semProjeto && (
            <Typography sx={{ fontSize: 12, color: '#BA7517', mt: 0.5 }}>
              Carga contratada: <strong>{formatDias(contrato.carga_total)}</strong>
              {contrato.workshops_vendidos > 0 && <> · Workshops vendidos: <strong>{contrato.workshops_vendidos}</strong></>}
              {' · '}Ainda sem projeto: falta adicionar o primeiro na tela Projetos.
            </Typography>
          )}
        </Box>
      </Box>

      <Collapse in={aberto} unmountOnExit>
        <Box sx={{ ml: 5, mt: 1.2, pl: 2, borderLeft: '3px solid #eef0f3' }}>
          {contrato.projetos.length === 0 ? (
            <Typography sx={{ fontSize: 12.5, color: '#64748b' }}>
              Nenhum projeto neste contrato ainda. Quem tem o acesso “Iniciar projeto” adiciona o primeiro na tela Projetos.
            </Typography>
          ) : (
            <>
              {contrato.projetos.map((p) => (
                <Box key={p.id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', py: 0.7, borderTop: '1px solid #f4f5f7', '&:first-of-type': { borderTop: 'none' } }}>
                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1a1a2e' }}>{p.nome}</Typography>
                    <Typography sx={{ fontSize: 11.5, color: '#94a3b8' }}>{STATUS_PROJETO[p.status] || p.status}</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography sx={{ fontSize: 13, fontFamily: 'monospace', fontWeight: 700, color: '#1a1a2e' }}>
                      {formatDias(p.carga_usada)}
                      <Typography component="span" sx={{ fontSize: 11.5, color: '#94a3b8', fontFamily: 'inherit', fontWeight: 400 }}>
                        {contrato.carga_total > 0 ? ` (${Math.round((p.carga_usada / contrato.carga_total) * 100)}% do contrato)` : ''}
                      </Typography>
                    </Typography>
                    <Typography sx={{ fontSize: 11.5, color: '#64748b' }}>
                      {p.workshops_usados} {p.workshops_usados === 1 ? 'workshop usado' : 'workshops usados'}
                    </Typography>
                  </Box>
                </Box>
              ))}
              <Typography sx={{ fontSize: 11.5, color: '#94a3b8', mt: 0.8 }}>
                Todos os projetos consomem da mesma carga. Livre no contrato: {formatDias(Math.max(saldo, 0))}.
              </Typography>
            </>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

export default function ContratosCarga({ contratos, mes, ano, nomeMes }) {
  if (!contratos || contratos.length === 0) return null;
  const aguardando = contratos.filter((c) => c.situacao === 'aguardando_projeto').length;
  return (
    <Card sx={{ mb: 3, borderRadius: 3 }}>
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 2, flexWrap: 'wrap' }}>
          <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Contratos com vários projetos</Typography>
          {aguardando > 0 && (
            <Chip
              size="small" label={`${aguardando} aguardando projeto`}
              sx={{ height: 22, fontSize: 11.5, fontWeight: 700, color: '#BA7517', backgroundColor: '#FAEEDA' }}
            />
          )}
        </Box>
        <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
          {contratos.map((c) => <LinhaContrato key={c.id} contrato={c} mes={mes} ano={ano} nomeMes={nomeMes} />)}
        </Box>
      </CardContent>
    </Card>
  );
}
